import { TaskModel } from '@bryntum/gantt';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { ProjectId, TaskId } from 'api/models';
import { Project, ProjectPhase } from 'api/project';
import { TaskAPI } from 'api/tasks';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import { confirm } from 'components/atoms/modal';
import { PermissionRuleName } from 'constant';
import { useProjectPhaseByProjectId } from 'data/projects';
import { updateProjectPhaseDatesAction } from 'data/projects/actions';
import {
  createDependencySagaAction,
  dependencyRemovedAction,
  updateTaskTimelineSagaAction,
} from 'data/tasks/actions';
import { useTasksByProjectId } from 'data/tasks/hooks';
import {
  createDependencies,
  deleteDependencies,
  updateTasksDates,
} from 'data/tasks/slice';
import { usePermissionForRule } from 'hooks/permissions/use-permission';
import { useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useTranslate } from 'hooks/use-translation';
import { useGetAlignPhaseDatesSetting } from 'queries/advanced-settings/advanced-settings';
import { getProjectDetailQueryKey } from 'queries/common';
import {
  useCreatePhaseDependencyMutation,
  useDeletePhaseDependencyMutation,
} from 'queries/project-phase';
import {
  addDays,
  EntityDateShiftUpdate,
  EntityType,
  getChildNodeIds,
  getCurrentDuration,
  getDateShiftsForEntity,
} from 'utils/date-shift';
import {
  filterTaskUpdates,
  formatDate,
  GanttType,
  getDueDateForGantt,
  getGanttPhaseId,
  getGanttTaskId,
  getPhaseList,
  getTaskList,
} from '../helpers';

type CreateDependencyEventPayload = {
  source: TaskModel;
  target: TaskModel;
};

type GanttEvent = {
  name: string;
  payload: TaskModel | CreateDependencyEventPayload;
};

export const useGanttEventsConfig = ({ projectId, ganttRef }) => {
  /**
   * Component state used to trigger event handlers
   * since Gantt is caching the event handler
   * due to which data models are cached resulting in unexpected behaviour.
   */
  const [event, setEvent] = useState(null as unknown as GanttEvent);
  const navigateToTask = useNavigateToTask();
  const dispatch = useDispatch();
  const projectPhases = useProjectPhaseByProjectId(projectId);
  const tasks = useTasksByProjectId(projectId);
  const queryClient = useQueryClient();
  const translate = useTranslate();

  const alignPhaseDates = useGetAlignPhaseDatesSetting();

  const { mutateAsync: createPhaseDependency } =
    useCreatePhaseDependencyMutation();

  const { mutateAsync: deletePhaseDependency } =
    useDeletePhaseDependencyMutation();

  const updateGanttDates = useCallback(
    (ganttId, currentEntity, startDate, dueDate) => {
      const { taskStore, project } = ganttRef?.instance;
      const record = taskStore.getById(ganttId);
      let newProjectStartDate = project.startDate;
      let newProjectEndDate = project.endDate;

      if (!record) {
        return;
      }

      if (startDate) {
        if (moment(startDate).isBefore(moment(project.startDate))) {
          project.setStartDate(startDate);
          newProjectStartDate = startDate;
        }
      }
      if (dueDate) {
        if (moment(dueDate).isAfter(moment(project.endDate))) {
          newProjectEndDate = dueDate;
        }
      }
      ganttRef.instance.setTimeSpan(
        new Date(newProjectStartDate),
        new Date(newProjectEndDate),
      );
      record.setStartDate(formatDate(startDate || currentEntity.startDate));
      record.setEndDate(getDueDateForGantt(dueDate || currentEntity.dueDate));
    },
    [ganttRef],
  );

  const updateProjectPhases = useCallback(
    (updates: EntityDateShiftUpdate[]) => {
      const project = queryClient.getQueryData<Project>(ProjectId(projectId));

      if (project) {
        const { projectPhases: phases } = project;
        const updatedDependentPhases: { [id: number]: ProjectPhase } = {};
        const updatedPhases = phases.map((phase) => {
          const update = updates.find((u) => u.id === phase.projectPhaseId);

          if (update != null) {
            const { startDate, dueDate, dependencyFor } = update;
            const _update = {} as Partial<ProjectPhase>;

            if (startDate) {
              _update.startDate = moment(startDate).format(API_DATE_FORMAT);
            }
            if (dueDate) {
              _update.dueDate = moment(dueDate).format(API_DATE_FORMAT);
            }
            if (dependencyFor) {
              dependencyFor.forEach((_dependencyFor) => {
                const _phase = phases.find(
                  ({ projectPhaseId }) => projectPhaseId === _dependencyFor,
                );

                const _phaseUpdate =
                  updatedDependentPhases[`${dependencyFor}`] || _phase;

                if (_phase) {
                  updatedDependentPhases[_dependencyFor] = {
                    ..._phaseUpdate,
                    dependentProjectPhases: [
                      ...(_phaseUpdate.dependentProjectPhases || []),
                      phase,
                    ],
                  };
                }
              });
            }

            return {
              ...phase,
              ..._update,
            };
          }

          return phase;
        });

        const finalUpdatedPhases = updatedPhases.map((_phase) => ({
          ..._phase,
          dependentProjectPhases: (
            updatedDependentPhases[_phase.projectPhaseId] ?? _phase
          ).dependentProjectPhases,
        }));

        const queryKey = getProjectDetailQueryKey(ProjectId(projectId));

        queryClient.setQueryData<Project>(queryKey, () => ({
          ...project,
          projectPhases: finalUpdatedPhases,
        }));
      }
    },
    [projectId, queryClient],
  );

  const removeProjectPhaseDependencies = useCallback(
    (phaseId: number, dependentPhaseId: number) => {
      const project = queryClient.getQueryData<Project>(ProjectId(projectId));

      if (project) {
        const { projectPhases: phases } = project;

        let targetPhase = phases.find(
          ({ projectPhaseId }) => projectPhaseId === phaseId,
        );

        if (targetPhase) {
          const dependentPhases =
            targetPhase.dependentProjectPhases?.filter(
              ({ projectPhaseId }) => projectPhaseId !== dependentPhaseId,
            ) ?? [];

          if (dependentPhases?.length <= 0) {
            delete targetPhase.dependentProjectPhases;
          } else {
            targetPhase = {
              ...targetPhase,
              dependentProjectPhases: dependentPhases,
            };
          }

          const queryKey = getProjectDetailQueryKey(ProjectId(projectId));

          queryClient.setQueryData<Project>(queryKey, () => ({
            ...project,
            projectPhases: phases.map((_phase) =>
              _phase.projectPhaseId === phaseId
                ? (targetPhase as ProjectPhase)
                : _phase,
            ),
          }));
        }
      }
    },
    [projectId, queryClient],
  );

  const onDrop = useCallback(
    (entity: TaskModel) => {
      const { id: entityId, type: entityType } = entity.get('data');
      const phaseList = getPhaseList(projectPhases);
      const taskList = getTaskList(tasks, phaseList);
      const isTask =
        entityType === GanttType.TaskBar ||
        entityType === GanttType.MilestoneBar;

      const currentEntity = isTask ? taskList[entityId] : phaseList[entityId];

      const { startDate: currentStartDate, dueDate: currentDueDate } =
        currentEntity;
      const childNodeIds = getChildNodeIds(entityId, {
        ...phaseList,
        ...taskList,
      });
      const childNodes = {};

      childNodeIds.forEach((_id) => {
        childNodes[_id] = taskList[_id];
      });
      const duration = getCurrentDuration(
        entityId,
        currentStartDate,
        currentDueDate,
        childNodes,
      );
      const newStartDate = moment(entity.get('startDate'));
      const newDueDate = addDays(newStartDate, duration - 1);

      const [phaseUpdates, taskUpdates] = getDateShiftsForEntity(
        phaseList,
        taskList,
        {
          id: entityId,
          startDate: formatDate(newStartDate),
          dueDate: formatDate(newDueDate),
          type: isTask ? EntityType.Task : EntityType.Phase,
          depth: currentEntity.depth,
        },
        {
          alignPhaseWithTask: alignPhaseDates,
        },
      );

      Object.values(phaseUpdates)?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttPhaseId(id);

        updateGanttDates(ganttId, phaseList[id], startDate, dueDate);
      });
      Object.values(taskUpdates)?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttTaskId(id);

        updateGanttDates(ganttId, taskList[id], startDate, dueDate);
      });
      entity.set('isCalculatedStartDate', false);

      if (isTask) {
        dispatch(
          updateTaskTimelineSagaAction({
            projectId,
            taskId: Number(entityId),
            data: {
              startDate: newStartDate.format(API_DATE_FORMAT),
              dueDate: newDueDate.format(API_DATE_FORMAT),
            },
            options: {
              skipStoreUpdate: true,
            },
          }),
        );
      } else {
        dispatch(
          updateProjectPhaseDatesAction({
            data: {
              projectId,
              phaseId: Number(entityId),
              startDate: newStartDate.format(API_DATE_FORMAT),
              dueDate: newDueDate.format(API_DATE_FORMAT),
            },
          }),
        );
        phaseUpdates[entityId] = {
          id: entityId,
          startDate: newStartDate,
          dueDate: newDueDate,
        };
      }
      const filteredUpdates = filterTaskUpdates(taskUpdates, tasks);

      if (isTask) {
        filteredUpdates[entityId] = {
          ...filteredUpdates[entityId],
          startDate: taskUpdates[entityId].startDate,
        };
      }

      dispatch(updateTasksDates(Object.values(filteredUpdates)));
      updateProjectPhases(Object.values(phaseUpdates));
    },
    [
      alignPhaseDates,
      dispatch,
      projectId,
      projectPhases,
      tasks,
      updateGanttDates,
      updateProjectPhases,
    ],
  );

  /**
   * only tasks can be resized and NOT the phases
   * (as per our implementation, not limitation from Bryntum)
   * hence the following handler works assuming task is resized.
   */
  const onResize = useCallback(
    (entity: TaskModel) => {
      const { id: entityId, type: entityType } = entity.get('data');
      const phaseList = getPhaseList(projectPhases);
      const taskList = getTaskList(tasks, phaseList);
      const isTask =
        entityType === GanttType.TaskBar ||
        entityType === GanttType.MilestoneBar;
      const newDueDate = moment(entity.get('endDate'));

      const [phaseUpdates, taskUpdates] = getDateShiftsForEntity(
        phaseList,
        taskList,
        {
          id: entityId,
          dueDate: formatDate(newDueDate),
          type: isTask ? EntityType.Task : EntityType.Phase,
          depth: taskList[entityId].depth,
        },
        {
          alignPhaseWithTask: alignPhaseDates,
        },
      );

      Object.values(phaseUpdates)?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttPhaseId(id);

        updateGanttDates(ganttId, phaseList[id], startDate, dueDate);
      });
      Object.values(taskUpdates)?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttTaskId(id);

        updateGanttDates(ganttId, taskList[id], startDate, dueDate);
      });
      entity.set('isCalculatedEndDate', false);

      dispatch(
        updateTaskTimelineSagaAction({
          projectId,
          taskId: Number(entityId),
          data: {
            dueDate: newDueDate.format(API_DATE_FORMAT),
          },
          options: {
            skipStoreUpdate: true,
          },
        }),
      );
      const filteredUpdates = filterTaskUpdates(taskUpdates, tasks);

      filteredUpdates[entityId] = {
        ...filteredUpdates[entityId],
        dueDate: taskUpdates[entityId].dueDate,
      };
      dispatch(updateTasksDates(Object.values(filteredUpdates)));
      updateProjectPhases(Object.values(phaseUpdates));
    },
    [
      alignPhaseDates,
      dispatch,
      projectId,
      projectPhases,
      tasks,
      updateGanttDates,
      updateProjectPhases,
    ],
  );

  const onDependencyCreate = useCallback(
    ({ source, target }: { source: TaskModel; target: TaskModel }) => {
      const phaseList = getPhaseList(projectPhases);
      const taskList = getTaskList(tasks, phaseList);
      const { id: targetEntityId } = target.get('data');
      const { id: sourceEntityId, type: sourceEntityType } = source.get('data');
      const isTask =
        sourceEntityType === GanttType.TaskBar ||
        sourceEntityType === GanttType.MilestoneBar;

      const sourceEntity = isTask
        ? taskList[sourceEntityId]
        : phaseList[sourceEntityId];

      const [phaseUpdates, taskUpdates] = getDateShiftsForEntity(
        phaseList,
        taskList,
        {
          id: sourceEntityId,
          dependencyFor: [
            ...(sourceEntity.dependencyFor || []),
            targetEntityId,
          ],
          type: isTask ? EntityType.Task : EntityType.Phase,
          depth: sourceEntity.depth,
        },
        {
          alignPhaseWithTask: alignPhaseDates,
        },
      );

      Object.values(phaseUpdates)?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttPhaseId(id);

        updateGanttDates(ganttId, phaseList[id], startDate, dueDate);
      });
      Object.values(taskUpdates)?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttTaskId(id);

        updateGanttDates(ganttId, taskList[id], startDate, dueDate);
      });
      if (isTask) {
        dispatch(
          createDependencySagaAction({
            projectId,
            taskId: Number(targetEntityId),
            dependentTaskId: Number(sourceEntityId),
          }),
        );
        dispatch(
          createDependencies([
            {
              data: { taskId: targetEntityId, dependentTaskId: sourceEntityId },
            },
          ]),
        );
      } else {
        createPhaseDependency({
          phaseId: targetEntityId,
          dependentPhaseId: sourceEntityId,
        });
      }
      dispatch(
        updateTasksDates(Object.values(filterTaskUpdates(taskUpdates, tasks))),
      );
      updateProjectPhases(Object.values(phaseUpdates));
    },
    [
      alignPhaseDates,
      createPhaseDependency,
      dispatch,
      projectId,
      projectPhases,
      tasks,
      updateGanttDates,
      updateProjectPhases,
    ],
  );

  const onDependencyDelete = useCallback(
    async ({ source, target }: { source: TaskModel; target: TaskModel }) => {
      const { id: targetEntityId } = target.get('data');
      const { id: sourceEntityId, type: sourceEntityType } = source.get('data');
      const isTask =
        sourceEntityType === GanttType.TaskBar ||
        sourceEntityType === GanttType.MilestoneBar;

      if (isTask) {
        await TaskAPI.removeDependentTask(projectId, targetEntityId, {
          dependentTaskIds: [sourceEntityId],
        });
        dispatch(
          dependencyRemovedAction({
            taskId: targetEntityId,
            dependentTaskId: sourceEntityId,
          }),
        );
        dispatch(
          deleteDependencies([
            {
              data: { taskId: targetEntityId, dependentTaskId: sourceEntityId },
            },
          ]),
        );
      } else {
        await deletePhaseDependency({
          phaseId: targetEntityId,
          dependentPhaseId: sourceEntityId,
        });
        removeProjectPhaseDependencies(targetEntityId, sourceEntityId);
      }
    },
    [
      deletePhaseDependency,
      dispatch,
      projectId,
      removeProjectPhaseDependencies,
    ],
  );

  const openTaskPane = useCallback(
    (taskRecord, cellElement: HTMLElement, target: HTMLElement) => {
      if (
        target.classList.contains('b-tree-expander') ||
        target.closest('.b-tree-expander')
      ) {
        return;
      }

      if (taskRecord.get('type') === GanttType.TaskBar) {
        navigateToTask(TaskId(projectId, taskRecord.get('data').id));
      }
    },
    [navigateToTask, projectId],
  );

  useEffect(() => {
    switch (event?.name) {
      case 'onDrop':
        onDrop(event.payload as TaskModel);
        break;
      case 'onResize':
        onResize(event.payload as TaskModel);
        break;
      case 'onDependencyCreate':
        onDependencyCreate(event.payload as CreateDependencyEventPayload);
        break;
      case 'onDependencyDelete':
        onDependencyDelete(event.payload as CreateDependencyEventPayload);
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);
  const { isAllowedForCurrentUser } = usePermissionForRule();

  return useMemo(
    () => ({
      listeners: {
        taskDrop: ({ taskRecords }: { taskRecords: TaskModel[] }): void => {
          setEvent({
            name: 'onDrop',
            payload: taskRecords[0],
          });
        },
        taskResizeEnd: ({ taskRecord }: { taskRecord: TaskModel }): void => {
          setEvent({
            name: 'onResize',
            payload: taskRecord,
          });
        },
        afterDependencyCreateDrop: ({
          data: { source, target, valid },
        }: {
          data: {
            source: TaskModel;
            target: TaskModel;
            valid: boolean;
          };
        }) => {
          if (!valid) {
            return;
          }
          setEvent({
            name: 'onDependencyCreate',
            payload: { source, target },
          });
        },

        dependencyClick: ({ dependency }) => {
          if (
            !isAllowedForCurrentUser(
              PermissionRuleName.TaskRemoveDependentTaskAction,
            )
          ) {
            return;
          }
          confirm({
            title: translate('timeline.dependency.delete.confirm.title'),
            content: translate('timeline.dependency.delete.confirm.content', {
              from: dependency.fromEvent.name,
              to: dependency.toEvent.name,
              b: (chunks) => <b>{chunks}</b>,
            }),
            onOk: () => {
              dependency.remove();
              setEvent({
                name: 'onDependencyDelete',
                payload: {
                  source: dependency.fromEvent,
                  target: dependency.toEvent,
                },
              });
            },
          });
        },

        cellClick({
          cellElement,
          record,
          target,
        }: {
          cellElement: HTMLElement;
          record: TaskModel;
          target: HTMLElement;
        }) {
          openTaskPane(record, cellElement, target);
        },
      },
    }),
    [isAllowedForCurrentUser, openTaskPane, translate],
  );
};
